/**
 * The model — win probabilities for the week, priced off whatever golf is
 * already in the book.
 *
 * A Monte Carlo through the app's own engine: every simulation fills in the
 * holes nobody has played yet (the sampler is `seed-demo`'s — a normal around
 * the man's course handicap, clamped eagle to +5), settles every card, seeds
 * the championship flights off the simulated standings exactly the way the
 * week does, plays the final, and records who won. Run a couple of thousand
 * times, the counts are the field's chances.
 *
 * Two properties are load-bearing:
 *
 * SNAPSHOTS ARE DERIVED, NOT STORED. "The odds as of Monday night" means:
 * condition on the score rows of rounds dated on or before Monday, simulate
 * the rest, with the RNG seeded off the snapshot's own key. Yesterday's
 * snapshot therefore never changes and never needs saving — it is recomputed
 * bit-for-bit from the scores, which the app already keeps forever.
 *
 * HANDICAP MOSTLY CANCELS, BY CONSTRUCTION. Every game this week is net: the
 * sampler gives a 16 more strokes and the formats hand them straight back, so
 * before a shot is hit the field prices nearly flat — which is what a fair
 * handicapped event IS. The number that moves the model is real golf: a
 * settled card shifts its man because those points are no longer a coin toss.
 */

import { buildContext, computeAll, fillFlightGroups } from '../select'
import { hash32, mulberry32 } from './rank'
import { matchCounts } from './types'
import type { HoleScore, PlayerId, Tournament } from './types'

/** seed-demo's sampler, verbatim: the tail is a real tail, not a clamp. */
export function grossFor(
  par: number,
  courseHandicap: number,
  holeCount: number,
  rnd: () => number,
): number {
  const mean = courseHandicap / holeCount
  const u = Math.max(rnd(), 1e-9)
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rnd())
  const delta = Math.round(mean + z * 0.95)
  return Math.max(par - 2, Math.min(par + 5, par + delta))
}

export interface ModelRow {
  playerId: PlayerId
  /** Chance of winning the week, 0..1. */
  p: number
  /** The fair price for that chance — no margin. The house adds his own. */
  fair: number
}

export interface ModelSnapshot {
  rows: ModelRow[]
  sims: number
  /** Real score rows the simulation was conditioned on. */
  knownScores: number
}

/** A probability as the fair American price, capped where golf gets silly. */
export function fairAmerican(p: number): number {
  if (p <= 0.01) return 9900
  if (p >= 0.99) return -9900
  return p >= 0.5 ? -Math.round((100 * p) / (1 - p)) : Math.round((100 * (1 - p)) / p)
}

/**
 * The reusable per-sim machinery, priced once — contexts, handicaps and hole
 * lists do not change between simulations, only the dice do.
 */
function prepare(t: Tournament, known: HoleScore[]) {
  const championship = t.matches.find(
    (m) => m.config.kind === 'strokeNet' && m.config.flighted,
  )
  const rounds = t.matches
    .filter((m) => matchCounts(m))
    .map((m) => {
      const round = t.rounds.find((r) => r.id === m.roundId)
      if (!round) return null
      const ctx = buildContext(t, round, m, known)
      if (!ctx) return null
      return {
        matchId: m.id,
        round,
        holes: ctx.holes,
        handicaps: ctx.handicaps,
        players: ctx.groups.flatMap((g) => g.playerIds),
        isChamp: m.id === championship?.id,
        /** hole numbers each player has REAL scores for, so the dice skip them. */
        knownAt: new Set(
          known
            .filter((s) => s.roundId === round.id)
            .map((s) => `${s.playerId}~${s.holeNo}`),
        ),
      }
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
  return { championship, rounds }
}

/**
 * One priced snapshot. Deterministic for a given (doc, known scores, seedKey):
 * the same question always gets the same answer, which is what lets the day
 * tabs be recomputed instead of stored.
 *
 * `simsDone`/`onBatch` exist so a screen can run this in slices and keep the
 * thumb responsive; calling it once with the full count is the simple form.
 */
export function priceTheField(
  t: Tournament,
  known: HoleScore[],
  opts: { sims: number; seedKey: string },
): ModelSnapshot {
  const { championship, rounds } = prepare(t, known)
  const confirmedAll = t.groups.map((g) => ({ roundId: g.roundId, groupId: g.id }))
  const wins = new Map<PlayerId, number>()

  for (let i = 0; i < opts.sims; i++) {
    const rnd = mulberry32(hash32(`${t.settings.rngSeed}|model|${opts.seedKey}|${i}`))
    const sim: HoleScore[] = [...known]

    const deal = (pr: (typeof rounds)[number]) => {
      for (const pid of pr.players) {
        for (const h of pr.holes) {
          if (pr.knownAt.has(`${pid}~${h.number}`)) continue
          sim.push({
            roundId: pr.round.id,
            playerId: pid,
            holeNo: h.number,
            value: {
              state: 'holed',
              gross: grossFor(
                h.par,
                pr.handicaps[pid]?.courseHandicap ?? 0,
                pr.holes.length,
                rnd,
              ),
            },
            enteredBy: pid,
            updatedAt: 0,
            deviceId: 'model',
          })
        }
      }
    }

    for (const pr of rounds) if (!pr.isChamp) deal(pr)

    let finalDoc = t
    let confirmed = confirmedAll
    if (championship) {
      // Seed the flights off the simulated week, the way the real one will.
      const pass1 = computeAll(t, sim, confirmedAll)
      const groups = fillFlightGroups(t, championship.roundId, pass1.flights)
      finalDoc = { ...t, groups }
      confirmed = groups.map((g) => ({ roundId: g.roundId, groupId: g.id }))
      const champPr = rounds.find((pr) => pr.isChamp)
      if (champPr) deal(champPr)
    }

    const final = computeAll(finalDoc, sim, confirmed)
    const winner = final.standings.rows[0]?.playerId
    if (winner) wins.set(winner, (wins.get(winner) ?? 0) + 1)
  }

  const rows = t.players
    .filter((p) => p.active)
    .map((p) => {
      const w = wins.get(p.id) ?? 0
      const prob = opts.sims > 0 ? w / opts.sims : 0
      return { playerId: p.id, p: prob, fair: fairAmerican(prob) }
    })
    .sort((a, b) => b.p - a.p)

  return { rows, sims: opts.sims, knownScores: known.length }
}

/**
 * Which men made the top flight, once per simulated week.
 *
 * The parlay builder needs joint chances — "do these two BOTH make it" is
 * not the product of two singles, because the flights are a ranking and the
 * men are competing for the same four seats. So the simulation hands back
 * the membership itself and the screen counts whatever combination somebody
 * selects: one run, every ticket priced instantly, and correlations
 * included by construction rather than assumed away.
 */
export function flightSets(
  t: Tournament,
  known: HoleScore[],
  opts: { sims: number; seedKey: string },
): PlayerId[][] {
  const { championship, rounds } = prepare(t, known)
  const confirmedAll = t.groups.map((g) => ({ roundId: g.roundId, groupId: g.id }))
  const out: PlayerId[][] = []

  for (let i = 0; i < opts.sims; i++) {
    const rnd = mulberry32(hash32(`${t.settings.rngSeed}|flights|${opts.seedKey}|${i}`))
    const sim: HoleScore[] = [...known]
    for (const pr of rounds) {
      if (pr.isChamp) continue
      for (const pid of pr.players) {
        for (const h of pr.holes) {
          if (pr.knownAt.has(`${pid}~${h.number}`)) continue
          sim.push({
            roundId: pr.round.id,
            playerId: pid,
            holeNo: h.number,
            value: {
              state: 'holed',
              gross: grossFor(h.par, pr.handicaps[pid]?.courseHandicap ?? 0, pr.holes.length, rnd),
            },
            enteredBy: pid,
            updatedAt: 0,
            deviceId: 'model',
          })
        }
      }
    }
    // The flights are seeded off the week BEFORE Thursday, which is exactly
    // what a "makes the final flight" ticket is betting on.
    const seeded = computeAll(t, sim, confirmedAll)
    out.push(championship ? (seeded.flights[0]?.playerIds ?? []) : [])
  }
  return out
}

/**
 * One simulated week, told from both ends of the same draw.
 *
 * `flight` is who made Flight 1 and `cellar` who landed in the LAST flight —
 * both read off the same pre-Thursday seeding, because Scott's ruling is
 * that the parlay is about the flight draw, not the final standings: "it's
 * just final flight — top flight and Flight 3." One seeding pass per
 * simulated week hands back both ends, so a combined ticket carries its
 * correlations by construction — the same man cannot land in both flights.
 */
export function weekSets(
  t: Tournament,
  known: HoleScore[],
  opts: { sims: number; seedKey: string },
): { flight: PlayerId[]; cellar: PlayerId[] }[] {
  const { championship, rounds } = prepare(t, known)
  const confirmedAll = t.groups.map((g) => ({ roundId: g.roundId, groupId: g.id }))
  const out: { flight: PlayerId[]; cellar: PlayerId[] }[] = []

  for (let i = 0; i < opts.sims; i++) {
    const rnd = mulberry32(hash32(`${t.settings.rngSeed}|week|${opts.seedKey}|${i}`))
    const sim: HoleScore[] = [...known]

    for (const pr of rounds) {
      if (pr.isChamp) continue
      for (const pid of pr.players) {
        for (const h of pr.holes) {
          if (pr.knownAt.has(`${pid}~${h.number}`)) continue
          sim.push({
            roundId: pr.round.id,
            playerId: pid,
            holeNo: h.number,
            value: {
              state: 'holed',
              gross: grossFor(h.par, pr.handicaps[pid]?.courseHandicap ?? 0, pr.holes.length, rnd),
            },
            enteredBy: pid,
            updatedAt: 0,
            deviceId: 'model',
          })
        }
      }
    }

    if (!championship) {
      out.push({ flight: [], cellar: [] })
      continue
    }
    const seeded = computeAll(t, sim, confirmedAll)
    const flights = seeded.flights
    out.push({
      flight: flights[0]?.playerIds ?? [],
      cellar: flights[flights.length - 1]?.playerIds ?? [],
    })
  }
  return out
}

/**
 * What the model expects to happen in the golf still to come.
 *
 * The Flight-1 button maps a road a man could walk; this answers the other
 * question — what is LIKELY. Every unsettled contest is played out a few
 * hundred times through the real engine, and the side that wins most often
 * takes it; a quota block returns the order its men finish in most often,
 * by average finishing place. Ties come back as halves, which is honest:
 * two men the dice cannot separate should not be separated by a rounding.
 *
 * Returned in the shape the simulator's picks already use, so the answer
 * lands in the ordinary editor where it can be argued with rather than
 * being an oracle.
 */
export function predictOutcomes(
  t: Tournament,
  known: HoleScore[],
  opts: { sims: number; seedKey: string },
): { contests: Record<string, 'a' | 'b' | 'half'>; quota: Record<string, PlayerId[]> } {
  const { championship, rounds } = prepare(t, known)
  const confirmedAll = t.groups.map((g) => ({ roundId: g.roundId, groupId: g.id }))
  /** pairing id → [side A wins, side B wins, halves] */
  const tally = new Map<string, [number, number, number]>()
  /** block key → player id → summed finishing place */
  const places = new Map<string, Map<PlayerId, number>>()

  for (let i = 0; i < opts.sims; i++) {
    const rnd = mulberry32(hash32(`${t.settings.rngSeed}|predict|${opts.seedKey}|${i}`))
    const sim: HoleScore[] = [...known]
    for (const pr of rounds) {
      if (pr.isChamp) continue
      for (const pid of pr.players) {
        for (const h of pr.holes) {
          if (pr.knownAt.has(`${pid}~${h.number}`)) continue
          sim.push({
            roundId: pr.round.id,
            playerId: pid,
            holeNo: h.number,
            value: {
              state: 'holed',
              gross: grossFor(h.par, pr.handicaps[pid]?.courseHandicap ?? 0, pr.holes.length, rnd),
            },
            enteredBy: pid,
            updatedAt: 0,
            deviceId: 'model',
          })
        }
      }
    }
    const out = computeAll(t, sim, confirmedAll)

    for (const m of t.matches) {
      if (m.id === championship?.id) continue
      const entry = out.byMatch[m.id]
      if (!entry) continue
      const tabOf = (unitId: string) => entry.tabs.find((x) => x.unitId === unitId)

      if (m.config.kind === 'quota') {
        const groups = t.groups.filter((g) => g.roundId === m.roundId)
        const blocks =
          m.config.rankingScope === 'group'
            ? groups.map((g) => ({ key: `${m.id}~${g.id}`, ids: g.playerIds }))
            : [{ key: m.id, ids: groups.flatMap((g) => g.playerIds) }]
        for (const b of blocks) {
          const ranked = [...b.ids]
            .map((pid) => ({ pid, v: entry.tabs.find((x) => x.playerIds[0] === pid)?.rankValue ?? 0 }))
            .sort((x, y) => y.v - x.v)
          const acc = places.get(b.key) ?? new Map<PlayerId, number>()
          ranked.forEach((r, idx) => acc.set(r.pid, (acc.get(r.pid) ?? 0) + idx))
          places.set(b.key, acc)
        }
        continue
      }

      for (const p of t.pairings.filter((x) => x.matchId === m.id)) {
        const ta = tabOf(p.sides[0]!.id)
        const tb = tabOf(p.sides[1]!.id)
        if (!ta || !tb) continue
        const cur = tally.get(p.id) ?? [0, 0, 0]
        if (ta.rankValue > tb.rankValue) cur[0] += 1
        else if (tb.rankValue > ta.rankValue) cur[1] += 1
        else cur[2] += 1
        tally.set(p.id, cur)
      }
    }
  }

  const contests: Record<string, 'a' | 'b' | 'half'> = {}
  for (const [id, [a, b, h]] of tally) {
    contests[id] = h >= a && h >= b ? 'half' : a > b ? 'a' : 'b'
  }
  const quota: Record<string, PlayerId[]> = {}
  for (const [key, acc] of places) {
    quota[key] = [...acc.entries()].sort((x, y) => x[1] - y[1]).map(([pid]) => pid)
  }
  return { contests, quota }
}

/**
 * The score rows a snapshot may know about: everything on rounds dated on or
 * before the day, and nothing after — "the odds as of Monday night" must not
 * peek at Tuesday. `null` means now: everything that has actually been played.
 */
export function scoresThrough(
  t: Tournament,
  scores: HoleScore[],
  throughDate: string | null,
): HoleScore[] {
  if (throughDate === null) return scores
  const allowed = new Set(
    t.rounds.filter((r) => r.date && r.date <= throughDate).map((r) => r.id),
  )
  return scores.filter((s) => allowed.has(s.roundId))
}
